'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@common/components';
import { Input } from '@common/components/atom/Input/Input';
import { Spinner } from '@common/components/atom/Spinner/Spinner';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ENV } from '@/constants';

// 카카오맵 SDK services(Geocoder) — 이 컴포넌트에서만 쓰는 최소 타입.
interface KakaoAddressDocument {
  address_name: string;
  road_address: {
    address_name: string;
    zone_no: string;
    building_name: string;
  } | null;
  address: {
    address_name: string;
  } | null;
}
type KakaoStatus = 'OK' | 'ZERO_RESULT' | 'ERROR';
declare global {
  interface Window {
    kakao?: {
      maps: {
        load: (callback: () => void) => void;
        services: {
          Geocoder: new () => {
            addressSearch: (query: string, callback: (result: KakaoAddressDocument[], status: KakaoStatus) => void, options?: {
              size?: number;
            }) => void;
          };
          Status: {
            OK: KakaoStatus;
            ZERO_RESULT: KakaoStatus;
            ERROR: KakaoStatus;
          };
        };
      };
    };
  }
}
export interface AddressResult {
  /** 우편번호 (5자리) */
  zonecode: string;
  /** 도로명주소 우선, 없으면 지번주소 */
  address: string;
  /** 건물명 */
  buildingName: string;
}
interface AddressSearchProps {
  onComplete: (result: AddressResult) => void;
}
const SDK_URL = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${ENV.NEXT_PUBLIC_KAKAO_CLIENT_ID}&libraries=services&autoload=false`;
let sdkPromise: Promise<void> | null = null;
const loadKakaoMapsServices = () => {
  if (window.kakao?.maps?.services) {
    return Promise.resolve();
  }
  if (!sdkPromise) {
    sdkPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SDK_URL;
      script.onload = () => window.kakao?.maps.load(resolve);
      script.onerror = () => reject(new Error('주소 검색 모듈을 불러오지 못했어요.'));
      document.head.appendChild(script);
    });
  }
  return sdkPromise;
};
const toAddressResult = (addressDocument: KakaoAddressDocument): AddressResult => ({
  zonecode: addressDocument.road_address?.zone_no ?? '',
  address: addressDocument.road_address?.address_name ?? addressDocument.address?.address_name ?? addressDocument.address_name,
  buildingName: addressDocument.road_address?.building_name ?? ''
});
const searchAddress = (keyword: string): Promise<AddressResult[]> => loadKakaoMapsServices().then(() => new Promise<AddressResult[]>((resolve, reject) => {
  const kakao = window.kakao;
  if (!kakao) {
    reject(new Error('주소 검색 모듈을 불러오지 못했어요.'));
    return;
  }
  new kakao.maps.services.Geocoder().addressSearch(keyword, (result, status) => {
    if (status === kakao.maps.services.Status.OK) {
      resolve(result.map(toAddressResult));
    } else if (status === kakao.maps.services.Status.ZERO_RESULT) {
      resolve([]);
    } else {
      reject(new Error('주소 검색에 실패했어요.'));
    }
  }, {
    size: 30
  });
}));
export const AddressSearch = t0 => {
  const $ = _c(32);
  const {
    onComplete
  } = t0;
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  let t1;
  let t2;
  if ($[0] !== submittedQuery) {
    t1 = ["kakaoAddressSearch", submittedQuery];
    t2 = () => searchAddress(submittedQuery);
    $[0] = submittedQuery;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = submittedQuery.length > 0;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      retry: false
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: results,
    isFetching,
    isError
  } = useQuery(t4);
  let t5;
  bb0: {
    if (!submittedQuery) {
      t5 = "\uCC3E\uC73C\uC2DC\uB294 \uACF3\uC758 \uC815\uD655\uD55C \uC8FC\uC18C\uB97C \uC785\uB825\uD574\uC8FC\uC138\uC694.\n\xB7 \uB3C4\uB85C\uBA85\uC8FC\uC18C \uC608) \uD310\uAD50\uC5ED\uB85C 235\n\xB7 \uC9C0\uBC88\uC8FC\uC18C \uC608) \uC0BC\uD3C9\uB3D9 681";
      break bb0;
    }
    if (isError) {
      t5 = "\uC8FC\uC18C \uAC80\uC0C9\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694.\n\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.";
      break bb0;
    }
    if (results?.length === 0) {
      t5 = "\uAC80\uC0C9 \uACB0\uACFC\uAC00 \uC5C6\uC5B4\uC694.\n\uB3C4\uB85C\uBA85\uC8FC\uC18C\uB098 \uC9C0\uBC88\uC744 \uC815\uD655\uD788 \uC785\uB825\uD588\uB294\uC9C0 \uD655\uC778\uD574\uC8FC\uC138\uC694.\n\uC608) \uD310\uAD50\uC5ED\uB85C 235";
      break bb0;
    }
    t5 = "";
  }
  const guideMessage = t5;
  let t6;
  bb1: {
    if (isFetching) {
      let t7;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <div className="flex h-full items-center justify-center"><Spinner /></div>;
        $[7] = t7;
      } else {
        t7 = $[7];
      }
      t6 = t7;
      break bb1;
    }
    if (guideMessage) {
      let t7;
      if ($[8] !== guideMessage) {
        t7 = <div className="flex h-full flex-col items-center justify-center px-20 text-center"><p className="whitespace-pre-line font-400 text-Gray-500 typo-body4">{guideMessage}</p></div>;
        $[8] = guideMessage;
        $[9] = t7;
      } else {
        t7 = $[9];
      }
      t6 = t7;
      break bb1;
    }
    let t7;
    if ($[10] !== onComplete || $[11] !== results) {
      t7 = results?.map((result, index) => <li key={`${result.address}-${index}`}><button type="button" onClick={() => onComplete(result)} className="flex w-full items-start gap-8 border-b border-Gray-100 py-12 text-left">{result.zonecode && <span className="shrink-0 rounded-xsmall bg-Gray-100 px-8 py-4 font-500 text-Gray-700 typo-caption1">{result.zonecode}</span>}<span className="font-500 text-Gray-900 typo-body4">{result.address}{result.buildingName && ` (${result.buildingName})`}</span></button></li>);
      $[10] = onComplete;
      $[11] = results;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== t7) {
      t8 = <ul className="flex flex-col">{t7}</ul>;
      $[13] = t7;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    t6 = t8;
  }
  const content = t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event => setQuery(event.target.value);
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== query) {
    t8 = event_0 => {
      if (event_0.key === "Enter") {
        event_0.preventDefault();
        setSubmittedQuery(query.trim());
      }
    };
    $[16] = query;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== query || $[19] !== t8) {
    t9 = <Input dimensions="H40" value={query} onChange={t7} onKeyDown={t8} placeholder={"\uB3C4\uB85C\uBA85\uC8FC\uC18C \uB610\uB294 \uC9C0\uBC88\uC744 \uC815\uD655\uD788 \uC785\uB825"} className="flex-1" />;
    $[18] = query;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== query) {
    t10 = <Button dimensions="H40" theme="sub-line" onClick={() => setSubmittedQuery(query.trim())} className="shrink-0">검색</Button>;
    $[21] = query;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t9) {
    t11 = <div className="flex gap-4">{t9}{t10}</div>;
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <p className="font-400 text-Gray-500 typo-caption1">정확한 도로명주소나 지번을 입력해야 검색돼요. (건물명·부분 검색은 지원되지 않아요)</p>;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== content) {
    t13 = <div className="flex-1 overflow-y-auto">{content}</div>;
    $[27] = content;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t11 || $[30] !== t13) {
    t14 = <div className="flex h-[400px] w-full flex-col gap-8 mobile:px-20">{t11}{t12}{t13}</div>;
    $[29] = t11;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
};